import React, { useState, useEffect , useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  TrendingUp,
  TrendingDown,
  Package,
  CheckCircle,
  AlertTriangle,
  Eye,
  Edit,
  Trash2,
  Plus,
  Search,
  Filter,
  RefreshCw,
  DollarSign,
  Calendar,
  Building,
  User,
  MapPin,
  FileText,
  Download,
  Upload,
  HelpCircle,
  ArrowUpRight,
  ArrowDownRight,
  MoreHorizontal
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import ActionMenu from '../common/ActionMenu';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import { assetService } from '../../services/assetService';
import MaintenanceService from '../../services/maintenanceService';
import '../../styles/asset-lifecycle-modal.css';

// Asset Action Dropdown (same UX as ProjectManagement)
const AssetActionDropdown = ({ asset, onView, onEdit, onDelete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openUpward, setOpenUpward] = useState(false);
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleScroll = () => setIsOpen(false);

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScroll, true);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setOpenUpward(spaceBelow < 220);
    }
    setIsOpen(!isOpen);
  };

  return (
    <div
      ref={dropdownRef}
      className="relative"
      style={{ zIndex: isOpen ? 9999 : 'auto' }}
    >
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 bg-white rounded hover:bg-blue-50 hover:border-blue-500 transition-colors shadow-sm"
      >
        <MoreHorizontal size={16} className="text-blue-500" />
      </button>

      {isOpen && (
        <div
          className="fixed min-w-[180px] bg-white border border-gray-200 rounded-lg shadow-lg"
          style={{
            zIndex: 10001,
            [openUpward ? 'bottom' : 'top']: openUpward
              ? `${window.innerHeight -
                  buttonRef.current.getBoundingClientRect().top +
                  8}px`
              : `${buttonRef.current.getBoundingClientRect().bottom + 8}px`,
            left: `${buttonRef.current.getBoundingClientRect().left}px`,
            transform: 'translateX(-75%)',
          }}
        >
          <ul className="py-1 m-0 list-none">
            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsOpen(false);
                  onView(asset);
                }}
              >
                <Eye size={16} className="mr-2.5 text-gray-500" />
                View Asset
              </button>
            </li>

            <li>
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50"
                onClick={() => {
                  setIsOpen(false);
                  onEdit(asset);
                }}
              >
                <Edit size={16} className="mr-2.5 text-gray-500" />
                Edit Asset
              </button>
            </li>

            <li className="border-t border-gray-200">
              <button
                className="flex items-center w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50"
                onClick={() => {
                  setIsOpen(false);
                  onDelete(asset);
                }}
              >
                <Trash2 size={16} className="mr-2.5 text-red-600" />
                Delete Asset
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
};

const AssetLifecycle = () => {
  const navigate = useNavigate();

  // Add CSS animations for modal
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideInFromRight {
        from { transform: translateX(100%); }
        to { transform: translateX(0); }
      }
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      @keyframes spin {
        from { transform: rotate(0deg); }
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  const [activeTab, setActiveTab] = useState('lifecycle');
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const { toasts, removeToast, success, error: showError } = useToast();

  // Real asset lifecycle data calculated from assets
  const [assetLifecycleData, setAssetLifecycleData] = useState([]);

  // Utility function to transform asset data into lifecycle format
  const transformAssetToLifecycle = (asset, maintenanceData = []) => {
    const now = new Date();
    const purchaseDate = asset.purchaseDate
      ? new Date(asset.purchaseDate)
      : new Date(asset.createdAt);
    const commissionDate = asset.createdAt
      ? new Date(asset.createdAt)
      : purchaseDate;

    // Calculate useful life in days from depreciation settings
    const usefulLifeYears = asset.depreciation?.usefulLife || 10;
    const usefulLifeDays = usefulLifeYears * 365;
    const plannedRetirementDate = new Date(purchaseDate);
    plannedRetirementDate.setFullYear(
      plannedRetirementDate.getFullYear() + usefulLifeYears
    );

    // Calculate days in use
    const daysInUse = Math.floor(
      (now - commissionDate) / (1000 * 60 * 60 * 24)
    );
    const remainingDays = Math.max(
      0,
      Math.floor((plannedRetirementDate - now) / (1000 * 60 * 60 * 24))
    );

    // Determine lifecycle stage based on asset status
    let currentStage = 'Active Use';
    let lifecycleStatus = 'Active';
    if (asset.status === 'Disposed') {
      currentStage = 'Disposed';
      lifecycleStatus = 'Disposed';
    } else if (asset.status === 'Inactive') {
      currentStage = 'Retired';
      lifecycleStatus = 'Retired';
    } else if (asset.status === 'Under Maintenance') {
      currentStage = 'Active Use';
      lifecycleStatus = 'Active';
    } else if (remainingDays < 365) {
      currentStage = 'Planned Retirement';
      lifecycleStatus = 'Planning Retirement';
    }

    // Calculate stage progress (0-100%)
    let stageProgress = 0;
    if (daysInUse > 0 && usefulLifeDays > 0) {
      stageProgress = Math.min(
        100,
        Math.round((daysInUse / usefulLifeDays) * 100)
      );
    }

    // Get maintenance cycles count
    const maintenanceCycles = maintenanceData.filter(
      (m) => m.asset?.assetId === asset.assetId
    ).length;
    const nextMaintenance = asset.maintenance?.nextMaintenanceDate || null;

    return {
      id: asset._id,
      assetId: asset.assetId || `AST-${asset._id.slice(-6)}`,
      assetName: asset.name,
      category: asset.type || 'Other',
      currentStage,
      stageProgress,
      acquisitionDate: purchaseDate.toISOString().split('T')[0],
      commissionDate: commissionDate.toISOString().split('T')[0],
      plannedRetirement: plannedRetirementDate.toISOString().split('T')[0],
      actualRetirement:
        asset.status === 'Disposed' || asset.status === 'Inactive'
          ? commissionDate.toISOString().split('T')[0]
          : null,
      totalLifecycleDays: usefulLifeDays,
      daysInUse,
      remainingDays,
      lifecycleStatus,
      maintenanceCycles,
      nextMaintenance: nextMaintenance
        ? new Date(nextMaintenance).toISOString().split('T')[0]
        : null,
      depreciationRate: asset.depreciation?.rate || 10,
      currentValue: asset.currentValue || asset.cost || 0,
      acquisitionCost: asset.cost || 0,
      disposalValue:
        asset.depreciation?.residualValue ||
        (asset.cost ? asset.cost * 0.1 : 0),
      notes: asset.notes || '',
      asset: asset, // Keep reference to original asset
    };
  };

  // Load asset lifecycle data
  useEffect(() => {
    loadAssetLifecycleData();
  }, []);

  const loadAssetLifecycleData = async () => {
    setIsLoading(true);
    try {
      // Fetch all assets (using max allowed limit of 100)
      const assetsResponse = await assetService.getAssets({ limit: 100 });
      const assets = assetsResponse.data?.assets || [];

      // Fetch maintenance records (using max allowed limit of 100)
      let maintenanceRecords = [];
      try {
        const maintenanceResponse =
          await MaintenanceService.getMaintenanceRecords({ limit: 100 });
        maintenanceRecords = maintenanceResponse.data || [];
      } catch (err) {
        console.error('Error loading maintenance records:', err);
      }

      // Transform assets to lifecycle format
      const lifecycleData = assets.map((asset) =>
        transformAssetToLifecycle(asset, maintenanceRecords)
      );
      setAssetLifecycleData(lifecycleData);
    } catch (error) {
      console.error('Error loading asset lifecycle data:', error);
      showError('Failed to load asset lifecycle data');
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate statistics
  const stats = React.useMemo(() => {
    if (assetLifecycleData.length === 0) {
      return {
        totalAssets: 0,
        activeAssets: 0,
        planningRetirement: 0,
        retiredAssets: 0,
        totalValue: 0,
        averageLifecycleDays: 0,
      };
    }

    return {
      totalAssets: assetLifecycleData.length,
      activeAssets: assetLifecycleData.filter(
        (a) => a.lifecycleStatus === 'Active'
      ).length,
      planningRetirement: assetLifecycleData.filter(
        (a) => a.currentStage === 'Planned Retirement'
      ).length,
      retiredAssets: assetLifecycleData.filter(
        (a) =>
          a.lifecycleStatus === 'Retired' || a.lifecycleStatus === 'Disposed'
      ).length,
      totalValue: assetLifecycleData.reduce(
        (sum, a) => sum + (a.currentValue || 0),
        0
      ),
      averageLifecycleDays:
        assetLifecycleData.length > 0
          ? assetLifecycleData.reduce(
              (sum, a) => sum + (a.totalLifecycleDays || 0),
              0
            ) / assetLifecycleData.length
          : 0,
    };
  }, [assetLifecycleData]);

  // Filtered data
  const filteredAssets = assetLifecycleData.filter((asset) => {
    const matchesSearch =
      searchTerm === '' ||
      asset.assetName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.assetId.toLowerCase().includes(searchTerm.toLowerCase()) ||
      asset.category.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const tabs = [
    { id: 'lifecycle', label: 'Asset Lifecycle', icon: <Clock size={18} /> },
    { id: 'planning', label: 'Acquisition Planning', icon: <Plus size={18} /> },
    {
      id: 'retirement',
      label: 'Retirement Planning',
      icon: <TrendingDown size={18} />,
    },
    {
      id: 'analytics',
      label: 'Lifecycle Analytics',
      icon: <TrendingUp size={18} />,
    },
  ];

  // Handle help button click
  const handleHelpClick = () => {
    const helpUrl = '/help/assets/lifecycle';
    const helpWindow = window.open(
      helpUrl,
      'helpWindow',
      'width=1200,height=800,scrollbars=yes,resizable=yes,menubar=no,toolbar=no,location=no,status=no'
    );
    if (helpWindow) {
      helpWindow.focus();
    }
  };

  // Handle refresh
  const handleRefresh = () => {
    loadAssetLifecycleData();
  };

  // Define columns for DataTable
  const columns = [
    {
      key: 'assetId',
      label: 'Asset ID',
      width: '120px',
    },
    {
      key: 'assetName',
      label: 'Asset',
      width: '250px',
      render: (value, row) => (
        <div>
          <div style={{ fontWeight: '500', color: '#1f2937' }}>
            {row.assetId}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>{value}</div>
        </div>
      ),
    },
    {
      key: 'category',
      label: 'Category',
      width: '120px',
      render: (category) => (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor:
              category === 'IT Equipment'
                ? '#dbeafe'
                : category === 'Photography'
                ? '#fef3c7'
                : category === 'Furniture'
                ? '#d1fae5'
                : category === 'Electronics'
                ? '#e0e7ff'
                : '#f3f4f6',
            color:
              category === 'IT Equipment'
                ? '#1e40af'
                : category === 'Photography'
                ? '#92400e'
                : category === 'Furniture'
                ? '#065f46'
                : category === 'Electronics'
                ? '#1e40af'
                : '#374151',
          }}
        >
          {category}
        </span>
      ),
    },
    {
      key: 'currentStage',
      label: 'Current Stage',
      width: '150px',
      render: (stage) => (
        <span
          style={{
            padding: '4px 8px',
            borderRadius: '4px',
            fontSize: '12px',
            fontWeight: '500',
            backgroundColor:
              stage === 'Active Use'
                ? '#d1fae5'
                : stage === 'Planned Retirement'
                ? '#fef3c7'
                : stage === 'Retired'
                ? '#fee2e2'
                : '#e0e7ff',
            color:
              stage === 'Active Use'
                ? '#065f46'
                : stage === 'Planned Retirement'
                ? '#92400e'
                : stage === 'Retired'
                ? '#991b1b'
                : '#1e40af',
          }}
        >
          {stage}
        </span>
      ),
    },
    {
      key: 'stageProgress',
      label: 'Progress',
      width: '120px',
      render: (progress) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '60px',
              height: '8px',
              backgroundColor: '#e5e7eb',
              borderRadius: '4px',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${progress}%`,
                height: '100%',
                backgroundColor:
                  progress > 75
                    ? '#ef4444'
                    : progress > 50
                    ? '#f59e0b'
                    : '#10b981',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <span style={{ fontSize: '12px', fontWeight: '500' }}>
            {progress}%
          </span>
        </div>
      ),
    },
    {
      key: 'acquisitionCost',
      label: 'Acquisition Cost',
      width: '130px',
      render: (cost) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <DollarSign size={14} style={{ color: '#10b981' }} />
          <span style={{ fontWeight: '500' }}>${cost.toLocaleString()}</span>
        </div>
      ),
    },
    {
      key: 'remainingDays',
      label: 'Days Remaining',
      width: '130px',
      render: (days) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <Clock
            size={14}
            style={{ color: days < 365 ? '#ef4444' : '#6b7280' }}
          />
          <span style={{ fontWeight: '500' }}>{days}</span>
        </div>
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      width: '80px',
      render: (value, asset) => (
      <AssetActionDropdown
      asset={asset}
      onView={handleView}
      onEdit={handleEdit}
      onDelete={handleDelete}
     />
    ),
  }
  ];

  // Handler functions
  const handleView = (asset) => {
    setSelectedAsset(asset);
    setShowViewModal(true);
  };

  const handleEdit = (lifecycleRecord) => {
    // Navigate to asset edit page
    if (lifecycleRecord.asset?._id) {
      navigate(`/assets/edit/${lifecycleRecord.asset._id}`);
    } else {
      showError('Unable to edit asset: Asset ID not found');
    }
  };

  const handleDelete = (lifecycleRecord) => {
    setSelectedAsset(lifecycleRecord);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    // Navigate to asset creation page
    navigate('/assets/create');
  };

  // Define action buttons for PageHeader (must be after handleAddNew is defined)
  const headerActions = [
    {
      label: '', // Empty label for icon-only square button
      icon: <HelpCircle size={18} />,
      onClick: handleHelpClick,
      variant: 'outline',
      tooltip: 'Help (Press ? for keyboard shortcut)',
    },
    {
      label: '', // Empty label for icon-only square button
      icon: <RefreshCw size={18} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh data',
    },
    {
      label: 'New Asset',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
      tooltip: 'Create new asset',
    },
  ];

  const handleDeleteConfirm = async () => {
    if (!selectedAsset?.asset?._id) {
      showError('Unable to delete: Asset ID not found');
      setShowDeleteModal(false);
      return;
    }

    setIsDeleting(true);
    try {
      await assetService.deleteAsset(selectedAsset.asset._id);
      success('Asset deleted successfully!');
      setShowDeleteModal(false);
      setSelectedAsset(null);
      // Reload data
      await loadAssetLifecycleData();
    } catch (error) {
      console.error('Error deleting asset:', error);
      showError(error.message || 'Failed to delete asset. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };
  
  // Mock trend data (replace later with real analytics)
const trends = {
  totalAssets: 6.4,
  activeAssets: 4.1,
  planningRetirement: -2.3,
  totalValue: 8.9,
};

// Trend helper
const getTrend = (value) => {
  if (value >= 0) {
    return {
      icon: <ArrowUpRight size={14} />,
      color: '#059669',
      label: `+${value}%`,
    };
  }
  return {
    icon: <ArrowDownRight size={14} />,
    color: '#DC2626',
    label: `${value}%`,
  };
};
  return (
    <>
      {/* Breadcrumb - Outside page-container to align with sidebar */}
      <PageBreadcrumb />
      {/* Page Header - Outside page-container to align with sidebar */}
      <PageHeader
        title="Asset Lifecycle Management"
        description="Track assets from acquisition to disposal with comprehensive lifecycle management"
        actions={headerActions}
      />
      <div
        className="page-container"
        style={{ width: '100%', maxWidth: '100%' }}
      >
        {/* Tabbed Navigation */}
        <div className="project-tabs">
          <div className="project-tabs-nav">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`project-tab ${
                  activeTab === tab.id ? 'active' : ''
                }`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="project-tab-icon">{tab.icon}</div>
                <div className="project-tab-content">
                  <div className="project-tab-label">{tab.label}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* KPI Cards – Dashboard style with trends */}
<div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mt-6 mb-8">

  {/* Total Assets */}
  <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
    <div className="h-1 w-full bg-[var(--primary-color)]" />

    <div
      className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
      style={{ color: getTrend(trends.totalAssets).color }}
    >
      {getTrend(trends.totalAssets).icon}
      {getTrend(trends.totalAssets).label}
    </div>

    <div className="flex items-center gap-3 p-4">
      <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
        <Package size={20} className="text-[#2D7DD2]" />
      </div>
      <div>
        <div className="text-[20px] font-bold text-[var(--text-primary)]">
          {stats.totalAssets}
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Total Assets
        </div>
      </div>
    </div>
  </div>

  {/* Active Assets */}
  <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
    <div className="h-1 w-full bg-[var(--primary-color)]" />

    <div
      className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
      style={{ color: getTrend(trends.activeAssets).color }}
    >
      {getTrend(trends.activeAssets).icon}
      {getTrend(trends.activeAssets).label}
    </div>

    <div className="flex items-center gap-3 p-4">
      <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
        <CheckCircle size={20} className="text-[#2D7DD2]" />
      </div>
      <div>
        <div className="text-[20px] font-bold text-[var(--text-primary)]">
          {stats.activeAssets}
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Active Assets
        </div>
      </div>
    </div>
  </div>

  {/* Planning Retirement */}
  <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
    <div className="h-1 w-full bg-[var(--primary-color)]" />

    <div
      className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
      style={{ color: getTrend(trends.planningRetirement).color }}
    >
      {getTrend(trends.planningRetirement).icon}
      {getTrend(trends.planningRetirement).label}
    </div>

    <div className="flex items-center gap-3 p-4">
      <div className="flex h-11 w-11 items-center justify-center bg-[#FEF3C7]">
        <AlertTriangle size={20} className="text-[#92400E]" />
      </div>
      <div>
        <div className="text-[20px] font-bold text-[var(--text-primary)]">
          {stats.planningRetirement}
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Planning Retirement
        </div>
      </div>
    </div>
  </div>

  {/* Total Value */}
  <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
    <div className="h-1 w-full bg-[var(--primary-color)]" />

    <div
      className="absolute right-4 top-3 flex items-center gap-1 text-[12px] font-semibold"
      style={{ color: getTrend(trends.totalValue).color }}
    >
      {getTrend(trends.totalValue).icon}
      {getTrend(trends.totalValue).label}
    </div>

    <div className="flex items-center gap-3 p-4">
      <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
        <DollarSign size={20} className="text-[#2D7DD2]" />
      </div>
      <div>
        <div className="text-[20px] font-bold text-[var(--text-primary)]">
          ${stats.totalValue.toLocaleString()}
        </div>
        <div className="text-[13px] text-[var(--text-secondary)]">
          Total Value
        </div>
      </div>
    </div>
  </div>

</div>

        {/* Main Content */}
        {activeTab === 'lifecycle' && (
          <div className="project-tab-content">
            <DataTable
              data={filteredAssets}
              columns={columns}
              title="Asset Lifecycle Records"
              searchable={true}
              sortable={true}
              exportable={true}
              pagination={true}
              pageSize={10}
              showActions={false}
              showCheckboxes={false}
              onView={handleView}
              onEdit={handleEdit}
              onDelete={handleDelete}
              searchPlaceholder="Search assets by name, ID, or category..."
              emptyMessage="No lifecycle records found. Click 'New Asset' to get started."
              initialHiddenColumns={['id']}
            />
          </div>
        )}

        {activeTab === 'planning' && (
          <div className="project-tab-content">
            {/* Acquisition Planning Stats */}
            <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-8 mt-4">
              {/* Recently Acquired Assets */}
              <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                <div className="h-1 w-full bg-[var(--primary-color)]" />
                <div className="flex items-center gap-3 p-4">
                  <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                    <Package size={20} className="text-[#2D7DD2]" />
                  </div>
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {
                      assetLifecycleData.filter((a) => {
                        const days = Math.floor(
                          (new Date() - new Date(a.acquisitionDate)) /
                          (1000 * 60 * 60 * 24)
                        );
                        return days <= 90;
                      }).length
                      }
                      </div>
                      <div className="text-[13px] text-[var(--text-secondary)]">
                        Recently Acquired (Last 90 Days)
                      </div>
                    </div>
                  </div>
           </div>
           {/* Recent Acquisition Cost */}
           <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-[#D6EFFF]">
                <DollarSign size={20} className="text-[#2D7DD2]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  $
                  {assetLifecycleData
                  .filter((a) => {
                    const days = Math.floor(
                      (new Date() - new Date(a.acquisitionDate)) /
                      (1000 * 60 * 60 * 24)
                    );
                    return days <= 90;
                  })
                  .reduce((sum, a) => sum + (a.acquisitionCost || 0), 0)
                  .toLocaleString()}
                  </div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Recent Acquisition Cost
                  </div>
                </div>
              </div>
           </div>
           {/* Assets Needing Replacement */}
           <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
            <div className="h-1 w-full bg-[var(--primary-color)]" />
            <div className="flex items-center gap-3 p-4">
              <div className="flex h-11 w-11 items-center justify-center bg-[#FEF3C7]">
                <AlertTriangle size={20} className="text-[#92400E]" />
              </div>
              <div>
                <div className="text-[20px] font-bold text-[var(--text-primary)]">
                  {
                  assetLifecycleData.filter(
                    (a) =>
                      a.stageProgress > 80 &&
                      a.lifecycleStatus === 'Active'
                    ).length
                  }
               </div>
              <div className="text-[13px] text-[var(--text-secondary)]">
                Assets Needing Replacement
              </div>
            </div>
          </div>
        </div>
      </div>

            {/* Recently Acquired Assets */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div
                className="card-header"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  padding: '16px 20px',
                }}
              >
                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Recently Acquired Assets (Last 90 Days)
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {assetLifecycleData.filter((a) => {
                  const daysSinceAcquisition = Math.floor(
                    (new Date() - new Date(a.acquisitionDate)) /
                      (1000 * 60 * 60 * 24)
                  );
                  return daysSinceAcquisition <= 90;
                }).length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Asset ID</th>
                          <th>Asset Name</th>
                          <th>Category</th>
                          <th>Acquisition Date</th>
                          <th>Acquisition Cost</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetLifecycleData
                          .filter((a) => {
                            const daysSinceAcquisition = Math.floor(
                              (new Date() - new Date(a.acquisitionDate)) /
                                (1000 * 60 * 60 * 24)
                            );
                            return daysSinceAcquisition <= 90;
                          })
                          .sort(
                            (a, b) =>
                              new Date(b.acquisitionDate) -
                              new Date(a.acquisitionDate)
                          )
                          .slice(0, 10)
                          .map((asset) => (
                            <tr key={asset.id}>
                              <td>{asset.assetId}</td>
                              <td style={{ fontWeight: '500' }}>
                                {asset.assetName}
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#1e40af',
                                  }}
                                >
                                  {asset.category}
                                </span>
                              </td>
                              <td>
                                {new Date(
                                  asset.acquisitionDate
                                ).toLocaleDateString()}
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                $
                                {asset.acquisitionCost?.toLocaleString() || '0'}
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#d1fae5',
                                    color: '#065f46',
                                  }}
                                >
                                  {asset.lifecycleStatus}
                                </span>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <Package size={48} className="text-muted mb-3" />
                    <p className="text-muted">
                      No assets acquired in the last 90 days
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Assets Needing Replacement */}
            <div className="card">
              <div
                className="card-header"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  padding: '16px 20px',
                }}
              >
                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Assets Approaching End of Life (Consider Replacement)
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {assetLifecycleData.filter(
                  (a) => a.stageProgress > 80 && a.lifecycleStatus === 'Active'
                ).length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Asset ID</th>
                          <th>Asset Name</th>
                          <th>Category</th>
                          <th>Lifecycle Progress</th>
                          <th>Remaining Days</th>
                          <th>Current Value</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetLifecycleData
                          .filter(
                            (a) =>
                              a.stageProgress > 80 &&
                              a.lifecycleStatus === 'Active'
                          )
                          .sort((a, b) => a.remainingDays - b.remainingDays)
                          .map((asset) => (
                            <tr key={asset.id}>
                              <td>{asset.assetId}</td>
                              <td style={{ fontWeight: '500' }}>
                                {asset.assetName}
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#1e40af',
                                  }}
                                >
                                  {asset.category}
                                </span>
                              </td>
                              <td>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '80px',
                                      height: '8px',
                                      backgroundColor: '#e5e7eb',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${asset.stageProgress}%`,
                                        height: '100%',
                                        backgroundColor:
                                          asset.stageProgress > 90
                                            ? '#ef4444'
                                            : '#f59e0b',
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  </div>
                                  <span
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {asset.stageProgress}%
                                  </span>
                                </div>
                              </td>
                              <td
                                style={{
                                  color:
                                    asset.remainingDays < 365
                                      ? '#ef4444'
                                      : '#6b7280',
                                  fontWeight: '500',
                                }}
                              >
                                {asset.remainingDays} days
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                ${asset.currentValue?.toLocaleString() || '0'}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(asset)}
                                  style={{ marginRight: '8px' }}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <CheckCircle size={48} className="text-success mb-3" />
                    <p className="text-muted">
                      All assets are within their useful life. No immediate
                      replacements needed.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'retirement' && (
          <div className="project-tab-content">
            {/* Retirement Planning Stats */}
            <div className="grid max-w-[1200px] mx-auto w-full grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-8 mt-4">
              {/* Scheduled for Retirement */}
              <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
                <div className="h-1 w-full bg-[var(--primary-color)]" />
                <div className="flex items-center gap-3 p-4">
                  <div className="flex h-11 w-11 items-center justify-center bg-[#FEF3C7]">
                    <Calendar size={20} className="text-[#92400E]" />
                  </div>
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {
                      assetLifecycleData.filter(
                        (a) => a.currentStage === 'Planned Retirement'
                      ).length
                      }
                    </div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Scheduled for Retirement
                  </div>
                </div>
              </div>
            </div>
            {/* Retiring Within 1 Year */}
            <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
              <div className="h-1 w-full bg-[var(--primary-color)]" />
              <div className="flex items-center gap-3 p-4">
                <div className="flex h-11 w-11 items-center justify-center bg-[#FEE2E2]">
                  <AlertTriangle size={20} className="text-[#EF4444]" />
                </div>
                <div>
                  <div className="text-[20px] font-bold text-[var(--text-primary)]">
                    {
                    assetLifecycleData.filter(
                      (a) =>
                        a.remainingDays < 365 &&
                        a.lifecycleStatus === 'Active'
                    ).length
                    }
                  </div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Retiring Within 1 Year
                  </div>
                </div>
              </div>
            </div>
            {/* Total Value at Risk */}
            <div className="relative border border-[var(--border-color)] bg-white shadow-md transition-all hover:-translate-y-1 hover:border-[var(--primary-color)] hover:shadow-[0_8px_25px_rgba(0,0,0,0.15)]">
              <div className="h-1 w-full bg-[var(--primary-color)]" />
              
              <div className="flex items-center gap-3 p-4">
                <div className="flex h-11 w-11 items-center justify-center bg-[#FEE2E2]">
                  <DollarSign size={20} className="text-[#EF4444]" />
                </div>
                <div>
                  <div className="text-[20px] font-bold text-[var(--text-primary)]">
                    $
                    {assetLifecycleData
                    .filter(
                      (a) =>
                        a.currentStage === 'Planned Retirement' ||
                      a.remainingDays < 365
                    )
                    .reduce((sum, a) => sum + (a.currentValue || 0), 0)
                    .toLocaleString()}
                  </div>
                  <div className="text-[13px] text-[var(--text-secondary)]">
                    Total Value at Risk
                  </div>
                </div>
              </div>
            </div>
          </div>
            {/* Assets Scheduled for Retirement */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div
                className="card-header"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  padding: '16px 20px',
                }}
              >
                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Assets Scheduled for Retirement
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {assetLifecycleData.filter(
                  (a) => a.currentStage === 'Planned Retirement'
                ).length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Asset ID</th>
                          <th>Asset Name</th>
                          <th>Category</th>
                          <th>Planned Retirement Date</th>
                          <th>Remaining Days</th>
                          <th>Current Value</th>
                          <th>Disposal Value</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetLifecycleData
                          .filter(
                            (a) => a.currentStage === 'Planned Retirement'
                          )
                          .sort(
                            (a, b) =>
                              new Date(a.plannedRetirement) -
                              new Date(b.plannedRetirement)
                          )
                          .map((asset) => (
                            <tr key={asset.id}>
                              <td>{asset.assetId}</td>
                              <td style={{ fontWeight: '500' }}>
                                {asset.assetName}
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#1e40af',
                                  }}
                                >
                                  {asset.category}
                                </span>
                              </td>
                              <td>
                                {new Date(
                                  asset.plannedRetirement
                                ).toLocaleDateString()}
                              </td>
                              <td
                                style={{
                                  color:
                                    asset.remainingDays < 90
                                      ? '#ef4444'
                                      : asset.remainingDays < 180
                                      ? '#f59e0b'
                                      : '#6b7280',
                                  fontWeight: '500',
                                }}
                              >
                                {asset.remainingDays} days
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                ${asset.currentValue?.toLocaleString() || '0'}
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                ${asset.disposalValue?.toLocaleString() || '0'}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(asset)}
                                  style={{ marginRight: '8px' }}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <CheckCircle size={48} className="text-success mb-3" />
                    <p className="text-muted">
                      No assets currently scheduled for retirement
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Assets Retiring Soon (Within 1 Year) */}
            <div className="card" style={{ marginBottom: '24px' }}>
              <div
                className="card-header"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  padding: '16px 20px',
                }}
              >
                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Assets Retiring Within 1 Year
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {assetLifecycleData.filter(
                  (a) =>
                    a.remainingDays < 365 &&
                    a.remainingDays > 0 &&
                    a.lifecycleStatus === 'Active'
                ).length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Asset ID</th>
                          <th>Asset Name</th>
                          <th>Category</th>
                          <th>Lifecycle Progress</th>
                          <th>Remaining Days</th>
                          <th>Planned Retirement</th>
                          <th>Current Value</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetLifecycleData
                          .filter(
                            (a) =>
                              a.remainingDays < 365 &&
                              a.remainingDays > 0 &&
                              a.lifecycleStatus === 'Active'
                          )
                          .sort((a, b) => a.remainingDays - b.remainingDays)
                          .map((asset) => (
                            <tr key={asset.id}>
                              <td>{asset.assetId}</td>
                              <td style={{ fontWeight: '500' }}>
                                {asset.assetName}
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#1e40af',
                                  }}
                                >
                                  {asset.category}
                                </span>
                              </td>
                              <td>
                                <div
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                  }}
                                >
                                  <div
                                    style={{
                                      width: '80px',
                                      height: '8px',
                                      backgroundColor: '#e5e7eb',
                                      borderRadius: '4px',
                                      overflow: 'hidden',
                                    }}
                                  >
                                    <div
                                      style={{
                                        width: `${asset.stageProgress}%`,
                                        height: '100%',
                                        backgroundColor:
                                          asset.stageProgress > 90
                                            ? '#ef4444'
                                            : '#f59e0b',
                                        transition: 'width 0.3s ease',
                                      }}
                                    />
                                  </div>
                                  <span
                                    style={{
                                      fontSize: '12px',
                                      fontWeight: '500',
                                    }}
                                  >
                                    {asset.stageProgress}%
                                  </span>
                                </div>
                              </td>
                              <td
                                style={{
                                  color:
                                    asset.remainingDays < 90
                                      ? '#ef4444'
                                      : asset.remainingDays < 180
                                      ? '#f59e0b'
                                      : '#6b7280',
                                  fontWeight: '500',
                                }}
                              >
                                {asset.remainingDays} days
                              </td>
                              <td>
                                {asset.plannedRetirement
                                  ? new Date(
                                      asset.plannedRetirement
                                    ).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                ${asset.currentValue?.toLocaleString() || '0'}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-primary"
                                  onClick={() => handleEdit(asset)}
                                  style={{ marginRight: '8px' }}
                                >
                                  View Details
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <CheckCircle size={48} className="text-success mb-3" />
                    <p className="text-muted">
                      No assets retiring within the next year
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Retired/Disposed Assets */}
            <div className="card">
              <div
                className="card-header"
                style={{
                  backgroundColor: '#f8f9fa',
                  borderBottom: '1px solid #dee2e6',
                  padding: '16px 20px',
                }}
              >
                <h5 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>
                  Retired and Disposed Assets
                </h5>
              </div>
              <div className="card-body" style={{ padding: '20px' }}>
                {assetLifecycleData.filter(
                  (a) =>
                    a.lifecycleStatus === 'Retired' ||
                    a.lifecycleStatus === 'Disposed'
                ).length > 0 ? (
                  <div style={{ overflowX: 'auto' }}>
                    <table className="table" style={{ marginBottom: 0 }}>
                      <thead>
                        <tr>
                          <th>Asset ID</th>
                          <th>Asset Name</th>
                          <th>Category</th>
                          <th>Status</th>
                          <th>Retirement Date</th>
                          <th>Original Cost</th>
                          <th>Disposal Value</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {assetLifecycleData
                          .filter(
                            (a) =>
                              a.lifecycleStatus === 'Retired' ||
                              a.lifecycleStatus === 'Disposed'
                          )
                          .sort((a, b) => {
                            const dateA = a.actualRetirement
                              ? new Date(a.actualRetirement)
                              : new Date(0);
                            const dateB = b.actualRetirement
                              ? new Date(b.actualRetirement)
                              : new Date(0);
                            return dateB - dateA;
                          })
                          .slice(0, 10)
                          .map((asset) => (
                            <tr key={asset.id}>
                              <td>{asset.assetId}</td>
                              <td style={{ fontWeight: '500' }}>
                                {asset.assetName}
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#1e40af',
                                  }}
                                >
                                  {asset.category}
                                </span>
                              </td>
                              <td>
                                <span
                                  style={{
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#fee2e2',
                                    color: '#991b1b',
                                  }}
                                >
                                  {asset.lifecycleStatus}
                                </span>
                              </td>
                              <td>
                                {asset.actualRetirement
                                  ? new Date(
                                      asset.actualRetirement
                                    ).toLocaleDateString()
                                  : 'N/A'}
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                $
                                {asset.acquisitionCost?.toLocaleString() || '0'}
                              </td>
                              <td style={{ fontWeight: '500' }}>
                                ${asset.disposalValue?.toLocaleString() || '0'}
                              </td>
                              <td>
                                <button
                                  className="btn btn-sm btn-outline-secondary"
                                  onClick={() => handleView(asset)}
                                >
                                  View
                                </button>
                              </td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center" style={{ padding: '40px' }}>
                    <CheckCircle size={48} className="text-muted mb-3" />
                    <p className="text-muted">No retired or disposed assets</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'analytics' && (
          <div className="project-tab-content">
            <div className="card">
              <div
                className="card-body text-center"
                style={{
                  minHeight: '300px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <div>
                  <TrendingUp size={48} className="text-muted mb-3" />
                  <h4 className="text-muted">Lifecycle Analytics</h4>
                  <p className="text-muted">Coming Soon!</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Asset"
        message={`Are you sure you want to delete asset ${selectedAsset?.assetName} (${selectedAsset?.assetId})? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
        isLoading={isDeleting}
      />

      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </>
  );
};

export default AssetLifecycle;
